import React, { useEffect, useMemo, useState } from 'react';
import { Add, Close, Delete, Edit, YouTube } from '@mui/icons-material';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { Col, Row } from 'antd';
import { get, has, map } from 'lodash';
import styled from 'styled-components';
import { v4 } from 'uuid';

import { DeleteModal } from 'components/Dialog';
import Loading from 'components/Loading';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import YouTubePlayer from 'components/ebook/YtPlayer';
import { apis } from 'utils/lib/apis';

const OverViewDist = styled.p`
  font-size: 20px;
  font-weight: 600;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const Card = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const ToggleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  background: #000;

  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    display: block;
  }
`;

const ActionBar = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  height: 36px;
  width: 36px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 17, 57, 0.9);
  color: #fff;
`;

const PreviewWrapper = styled.div`
  margin-top: 20px;
  width: 100%;
  max-width: 720px;
`;

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70vw;
  max-width: 760px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  padding: 24px;

  @media only screen and (max-width: 600px) {
    width: 92%;
    padding: 18px;
  }
`;

const EmptyState = styled.div`
  margin-top: 24px;
  padding: 32px 20px;
  border: 1px dashed #d9d9d9;
  border-radius: 14px;
  text-align: center;
  color: #666;
`;

const extractVideoID = (url) => {
  if (!url) {
    return null;
  }

  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const matches = url.trim().match(regex);

  return matches ? matches[1] : null;
};

const getThumbnail = (videoId) => {
  if (!videoId) {
    return '';
  }

  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

const initialForm = {
  id: '',
  url: '',
  status: 'active',
};

export default function OneGameYoutubeVideo() {
  const showAlert = useAlert();
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteToggle, setDeleteToggle] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [listFilter, setListFilter] = useState('all');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const videoId = useMemo(() => extractVideoID(form.url), [form.url]);
  const isEditMode = Boolean(form.id);

  const fetchAllYoutubeVideos = async () => {
    setIsLoading(true);

    try {
      const response = await apis.get('one_game', '/one_game_youtube_video/list');
      const data = Array.isArray(response) ? response : get(response, 'data', []);
      setVideos(Array.isArray(data) ? data : []);
    } catch (error) {
      showAlert('Unable to fetch YouTube videos.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllYoutubeVideos();
  }, []);

  const resetForm = () => {
    setForm(initialForm);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleUrlChange = (event) => {
    setForm((prev) => ({
      ...prev,
      url: event.target.value,
    }));
  };

  const handleStatusToggle = async (item, nextActive) => {
    const id = get(item, 'id');
    if (!id) {
      return;
    }
    const nextStatus = nextActive ? 'active' : 'inactive';
    setStatusUpdatingId(id);
    try {
      await apis.put('one_game', '/one_game_youtube_video/update-status', {
        body: { id, status: nextStatus },
      });
      setVideos((prev) =>
        prev.map((v) => (get(v, 'id') === id ? { ...v, status: nextStatus } : v))
      );
      showAlert(`Video marked ${nextStatus}.`, 'success');
    } catch (error) {
      showAlert('Unable to update status.', 'error');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!form.url.trim()) {
      showAlert('Please paste YouTube URL.', 'error');
      return;
    }

    if (!videoId) {
      showAlert('Please enter valid YouTube URL.', 'error');
      return;
    }

    const payload = {
      id: form.id || v4(),
      url: form.url.trim(),
      videoId,
      status: isEditMode
        ? form.status === 'inactive'
          ? 'inactive'
          : 'active'
        : 'active',
    };

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await apis.put('one_game', '/one_game_youtube_video/update', { body: payload });
        showAlert('YouTube video updated successfully.', 'success');
      } else {
        await apis.post('one_game', '/one_game_youtube_video/create', { body: payload });
        showAlert('YouTube video added successfully.', 'success');
      }

      closeModal();
      fetchAllYoutubeVideos();
    } catch (error) {
      showAlert(`Unable to ${isEditMode ? 'update' : 'add'} YouTube video.`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    const s = get(item, 'status', 'active');
    setForm({
      id: get(item, 'id', ''),
      url: get(item, 'url', ''),
      status: s === 'inactive' ? 'inactive' : 'active',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!has(selectedItem, 'id')) {
      setDeleteToggle(false);
      return;
    }

    setIsDeleting(true);

    try {
      await apis.del('one_game', `/one_game_youtube_video/delete/${selectedItem.id}`);
      showAlert('YouTube video deleted successfully.', 'success');
      setDeleteToggle(false);
      setSelectedItem(null);
      fetchAllYoutubeVideos();
    } catch (error) {
      showAlert('Unable to delete YouTube video.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const displayVideos = useMemo(() => {
    if (listFilter === 'all') {
      return videos;
    }
    return videos.filter((item) => {
      const s = get(item, 'status', 'active');
      return listFilter === 'active' ? s === 'active' : s === 'inactive';
    });
  }, [videos, listFilter]);

  return (
    <>
      <DeleteModal
        deleteToggle={deleteToggle}
        setDeleteToggle={setDeleteToggle}
        onDelete={handleDelete}
        onCancel={() => {
          setDeleteToggle(false);
          setSelectedItem(null);
        }}
        message={`Permanently delete this video? ${get(selectedItem, 'url', '')}`}
        loading={isDeleting}
      />

      <Modal
        open={isModalOpen}
        onClose={closeModal}
        style={{ height: '100%', width: '100%' }}
      >
        <ModalContent>
          <Row justify="space-between" align="middle">
            <Col>
              <OverViewDist style={{ marginBottom: 0 }}>
                {isEditMode ? 'Edit Youtube Video' : 'Add Youtube Video'}
              </OverViewDist>
            </Col>
            <Col>
              <IconButton onClick={closeModal} style={{ padding: 0 }}>
                <Close color="secondary" />
              </IconButton>
            </Col>
          </Row>

          <Row style={{ marginTop: 20 }}>
            <Col span={24}>
              <TextField
                fullWidth
                label="Youtube Link"
                placeholder="Paste youtube video url"
                value={form.url}
                onChange={handleUrlChange}
              />
            </Col>
          </Row>

          {form.url ? (
            <PreviewWrapper>
              <YouTubePlayer url={form.url} is_valid={Boolean(videoId)} playerId={`one-game-youtube-preview-${form.id || 'new'}`} />
            </PreviewWrapper>
          ) : null}

          <Row style={{ marginTop: 20, gap: 12 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? null : <YouTube />}
            >
              {isSubmitting ? <Loading white size={15} /> : null}
              {isEditMode ? 'Update Video' : 'Add Video'}
            </Button>

            <Button variant="outlined" onClick={closeModal}>
              Cancel
            </Button>
          </Row>
        </ModalContent>
      </Modal>

      <Row>
        <Col>
          <OverViewDist>1Game Video</OverViewDist>
        </Col>
      </Row>

      <Row align="middle" gutter={[12, 12]} style={{ marginTop: 8 }}>
        <Col>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
          >
            Add Youtube Videos
          </Button>
        </Col>
        <Col flex="auto">
          <FilterRow>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="one-game-yt-list-filter-label">Show</InputLabel>
              <Select
                labelId="one-game-yt-list-filter-label"
                label="Show"
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active only</MenuItem>
                <MenuItem value="inactive">Inactive only</MenuItem>
              </Select>
            </FormControl>
          </FilterRow>
        </Col>
      </Row>

      {isLoading ? (
        <div style={{ marginTop: 24 }}>
          <Loading />
        </div>
      ) : displayVideos.length === 0 ? (
        <EmptyState>No YouTube videos in this view.</EmptyState>
      ) : (
        <CardGrid>
          {map(displayVideos, (item) => {
            const currentVideoId = get(item, 'videoId') || extractVideoID(get(item, 'url', ''));
            const rowStatus = get(item, 'status', 'active');
            const isActiveRow = rowStatus === 'active';

            return (
              <Card key={item.id}>
                <ThumbnailWrapper>
                  <img src={getThumbnail(currentVideoId)} alt="youtube-thumbnail" />
                  <ActionBar>
                    <ActionButton type="button" onClick={() => handleEdit(item)}>
                      <Edit fontSize="small" />
                    </ActionButton>
                    <ActionButton
                      type="button"
                      onClick={() => {
                        setSelectedItem(item);
                        setDeleteToggle(true);
                      }}
                    >
                      <Delete fontSize="small" />
                    </ActionButton>
                  </ActionBar>
                </ThumbnailWrapper>
                <CardFooter>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                    {get(item, 'url', '').slice(0, 42)}
                    {get(item, 'url', '').length > 42 ? '…' : ''}
                  </span>
                  <ToggleWrap>
                    <Switch
                      size="small"
                      checked={isActiveRow}
                      disabled={statusUpdatingId === item.id}
                      onChange={(e) => handleStatusToggle(item, e.target.checked)}
                      inputProps={{ 'aria-label': isActiveRow ? 'Active' : 'Inactive' }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#424242', whiteSpace: 'nowrap' }}>
                      {isActiveRow ? 'Active' : 'Inactive'}
                    </span>
                  </ToggleWrap>
                </CardFooter>
              </Card>
            );
          })}
        </CardGrid>
      )}
    </>
  );
}
